import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { ACTIVITY_EVENT_TYPES, ACTIVITY_DESCRIPTIONS } from '@/types/activity-logs';

interface LogActivityParams {
  bookingId: string;
  eventType: string;
  actorType: 'customer' | 'internal' | 'system';
  actorId?: string;
  actorName?: string;
  actorEmail?: string;
  actorPhone?: string;
  description?: string;
  metadata?: Record<string, any>;
}

export const useActivityLogger = () => {
  const logActivity = async (params: LogActivityParams) => {
    try {
      console.log('[ACTIVITY-LOGGER] Logging activity:', params);
      
      const { data, error } = await supabase.rpc('log_booking_activity', {
        p_booking_id: params.bookingId,
        p_event_type: params.eventType,
        p_actor_type: params.actorType,
        p_actor_id: params.actorId || null,
        p_actor_name: params.actorName || null,
        p_actor_email: params.actorEmail || null,
        p_actor_phone: params.actorPhone || null,
        p_description: params.description || ACTIVITY_DESCRIPTIONS[params.eventType] || params.eventType,
        p_metadata: params.metadata || {}
      });

      if (error) {
        console.error('[ACTIVITY-LOGGER] Error logging activity:', error);
        throw error;
      }

      console.log('[ACTIVITY-LOGGER] Activity logged successfully:', data);
      return data;
    } catch (error) {
      console.error('[ACTIVITY-LOGGER] Failed to log activity:', error);
      // Don't show toast for activity logging failures to avoid user disruption
    }
  };

  // Convenience methods for common activities
  const logQuoteAdded = (bookingId: string, staffName: string, staffEmail: string, quoteAmount?: number) => {
    return logActivity({
      bookingId,
      eventType: ACTIVITY_EVENT_TYPES.QUOTE_ADDED,
      actorType: 'internal',
      actorName: staffName,
      actorEmail: staffEmail,
      metadata: { quote_amount: quoteAmount }
    });
  };

  const logQuoteViewed = (bookingId: string, customerName?: string, customerEmail?: string, quoteId?: string) => {
    return logActivity({
      bookingId,
      eventType: ACTIVITY_EVENT_TYPES.QUOTE_VIEWED,
      actorType: 'customer',
      actorName: customerName,
      actorEmail: customerEmail,
      metadata: { quote_id: quoteId }
    });
  };

  const logQuoteAccepted = (bookingId: string, customerName?: string, customerEmail?: string, quoteId?: string, quoteAmount?: number) => {
    return logActivity({
      bookingId,
      eventType: ACTIVITY_EVENT_TYPES.QUOTE_ACCEPTED,
      actorType: 'customer',
      actorName: customerName,
      actorEmail: customerEmail,
      metadata: { quote_id: quoteId, quote_amount: quoteAmount }
    });
  };

  const logQuoteRejected = (bookingId: string, customerName?: string, customerEmail?: string, quoteId?: string, reason?: string) => {
    return logActivity({
      bookingId,
      eventType: ACTIVITY_EVENT_TYPES.QUOTE_REJECTED,
      actorType: 'customer',
      actorName: customerName,
      actorEmail: customerEmail,
      metadata: { quote_id: quoteId, rejection_reason: reason }
    });
  };

  const logPaymentReceived = (bookingId: string, customerName: string, customerEmail: string, amount: number, currency: string = 'ZAR') => {
    return logActivity({
      bookingId,
      eventType: ACTIVITY_EVENT_TYPES.PAYMENT_RECEIVED,
      actorType: 'customer',
      actorName: customerName,
      actorEmail: customerEmail,
      metadata: { amount, currency }
    });
  };

  const logPaymentConfirmed = (bookingId: string, staffName: string, staffEmail: string) => {
    return logActivity({
      bookingId,
      eventType: ACTIVITY_EVENT_TYPES.PAYMENT_CONFIRMED,
      actorType: 'internal',
      actorName: staffName,
      actorEmail: staffEmail
    });
  };

  const logTrackingAdded = (bookingId: string, staffName: string, staffEmail: string, trackingUrl: string) => {
    return logActivity({
      bookingId,
      eventType: ACTIVITY_EVENT_TYPES.TRACKING_INFO_ADDED,
      actorType: 'internal',
      actorName: staffName,
      actorEmail: staffEmail,
      metadata: { tracking_url: trackingUrl }
    });
  };

  const logEmailSent = (bookingId: string, emailType: string, recipient: string) => {
    return logActivity({
      bookingId,
      eventType: ACTIVITY_EVENT_TYPES.EMAIL_SENT,
      actorType: 'system',
      actorName: 'System',
      metadata: { email_type: emailType, recipient }
    });
  };

  const logSecureLinkGenerated = (bookingId: string, staffName: string, staffEmail: string, tokenType: string, recipientEmail: string) => {
    return logActivity({
      bookingId,
      eventType: ACTIVITY_EVENT_TYPES.SECURE_LINK_GENERATED,
      actorType: 'internal',
      actorName: staffName,
      actorEmail: staffEmail,
      metadata: { token_type: tokenType, recipient_email: recipientEmail }
    });
  };

  return {
    logActivity,
    logQuoteAdded,
    logQuoteViewed,
    logQuoteAccepted,
    logQuoteRejected,
    logPaymentReceived,
    logPaymentConfirmed,
    logTrackingAdded,
    logEmailSent,
    logSecureLinkGenerated,
    ACTIVITY_EVENT_TYPES
  };
};
